// Core
import Amplify from '@aws-amplify/core';
import moment from 'moment';
import { useEffect, useRef } from 'react';

// i18n
import i18n from '../i18n/i18n';

export const capitalize = (string) => string.charAt(0).toUpperCase() + string.slice(1);

export const capitalizeAll = (string) => {
  const words = string.split(' ');
  const capitalizedWords = words.map((word) => capitalize(word));
  return capitalizedWords.join(' ');
};

/**
 * Mounts the full API url
 * @param apiSubdomain {string} The API subdomain
 * @returns {string} The full API url
 */
const mountApiUrl = (apiSubdomain) => process.env.REACT_APP_SPICE_API_URL.replace('[SUBDOMAIN]', apiSubdomain);

/** Configures the AWS Amplify */
export const configureAws = () => {
  // The endpoints
  const endpoints = [
    {
      name: 'customer',
      endpoint: mountApiUrl(process.env.REACT_APP_SPICE_API_SUBDOMAIN_CUSTOMER),
      region: process.env.REACT_APP_AWS_REGION,
    },
    {
      name: 'user',
      endpoint: mountApiUrl(process.env.REACT_APP_SPICE_API_SUBDOMAIN_USER),
      region: process.env.REACT_APP_AWS_REGION,
    },
    {
      name: 'modelRoll',
      endpoint: mountApiUrl(process.env.REACT_APP_SPICE_API_SUBDOMAIN_MODEL_ROLL),
      region: process.env.REACT_APP_AWS_REGION,
    },
    {
      name: 'modelPropX',
      endpoint: mountApiUrl(process.env.REACT_APP_SPICE_API_SUBDOMAIN_MODEL_PROPX),
      region: process.env.REACT_APP_AWS_REGION,
    },
    {
      name: 'monitoring',
      endpoint: mountApiUrl(process.env.REACT_APP_SPICE_API_SUBDOMAIN_MONITORING),
      region: process.env.REACT_APP_AWS_REGION,
    },
    {
      name: 'application',
      endpoint: mountApiUrl(process.env.REACT_APP_SPICE_API_SUBDOMAIN_APPLICATION),
      region: process.env.REACT_APP_AWS_REGION,
    },
  ];

  // The config
  const config = {
    Auth: {
      mandatorySignIn: true,
      region: process.env.REACT_APP_AWS_REGION,
      userPoolId: process.env.REACT_APP_AWS_COGNITO_USER_POOL_ID,
      identityPoolId: process.env.REACT_APP_AWS_COGNITO_IDENTITY_POOL_ID,
      userPoolWebClientId: process.env.REACT_APP_AWS_COGNITO_USER_POOL_WEB_CLIENT_ID,
    },
    API: {
      endpoints,
    },
  };

  // Configuring
  Amplify.configure(config);
};

// Theme CSS classes
export const appThemeClasses = {
  default: 'ThemeDefault',
  propx: 'ThemePropX',
};

/** Callback to sort strings */
export const sortString = (a, b) => {
  if (a > b) {
    return 1;
  }
  if (b > a) {
    return -1;
  }
  return 0;
};

/** Callback to sort numbers for ascending order */
export const sortNumber = (a, b) => a - b;

/** Callback to sort numbers for descending order */
export const sortNumberDescending = (a, b) => b - a;

/**
 * set a timestamp to a date string
 * @param timestamp {number} Timestamp to be converted
 * @param formatted {boolean} [true] for a "mm/dd at HHhmm" date string
 * @param fullMonth {boolean} [true] for a "dd mmm at HH:mmA" date string
 * @return {string} date string
 */
// @todo eliminate this, moment can solve this very simply in the components
export const timestampToDate = (timestamp, fullMonth = false) => {
  let date;
  const momentInstance = moment.unix(timestamp / 1000);

  if (fullMonth) {
    date = `${momentInstance.format('lll')}`;
  } else {
    date = `${momentInstance.format('L LT')}`;
  }
  return date;
};

export const timestampToDateFormat = (timestamp) => {
  const momentInstance = moment.unix(timestamp / 1000);
  const locale = i18n.language;
  const date = momentInstance.format('L').split('/');
  const time = momentInstance.format('LT');

  let day;
  let month;
  let hour;
  let minute;
  let dateFormatted;

  switch (locale) {
    case 'pt_BR':
    case 'es_ES':
      [day, month] = date;
      [hour, minute] = time.split(':');
      dateFormatted = `${day}/${month}, ${hour}h${minute}min`;
      break;
    case 'en_US':
      [month, day] = date;
      const timeArray = time.split(':');

      if (time.slice(-2) === 'AM') {
        [hour] = timeArray;
        if (hour === '12') hour = '0';
        if (Number(hour) < 10) hour = `0${hour}`;
      } else {
        hour = String(Number(timeArray[0]) + 12);
        if (hour === '24') hour = '12';
      }
      minute = timeArray[1].slice(0, 2);
      dateFormatted = `${month}/${day}, ${hour}h${minute}min`;
      break;
    default:
      break;
  }

  return dateFormatted;
};

export function formatNumberWithCommas(number) {
  const locale = i18n.language;

  let formattedNumber;
  if (locale === 'pt_BR') {
    formattedNumber = number.toLocaleString('pt-BR');
  } if (locale === 'en_US') {
    formattedNumber = number.toLocaleString('en-US');
  } if (locale === 'es_ES') {
    formattedNumber = number.toLocaleString('es-AR');
  }
  return formattedNumber;
}

/** Returns time elapsed since timestamp (seconds) */
export const timeElapsed = (timestamp, timestampEnd = null) => {
  const endTime = timestampEnd || Date.now();
  const timeDiff = endTime - timestamp;

  const hours = Math.round(timeDiff / 3600000);
  const minutes = Math.round((timeDiff % 3600000) / 60000);

  if (hours < 24) {
    if (hours >= 1 && minutes >= 1) {
      return `${hours}h${minutes}min`;
    }
    if (hours >= 1) {
      return `${hours} hours`;
    }
    return `${minutes} min`;
  }
  const days = Math.round(hours / 24);
  return `${days} days`;
};

export const secondsToClock = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/** Converts date string to localized date */
export const dateToLocale = (date) => {
  const momentInstance = moment(date);
  return `${momentInstance.format('L')} ${momentInstance.format('LT')}`;
};

/** Converts user status to readable data */
export const parseUserStatus = (status) => (status === 'active' ? 'Active' : 'Invited');

/** Checks if two objects are equal */
export const objectsEqual = (o1, o2) => (
  Object.keys(o1).length === Object.keys(o2).length && Object.keys(o1).every((p) => o1[p] === o2[p])
);

/** Returns query string variable value */
export const getQueryVariable = (queryString, variable) => {
  const query = queryString.substring(1);
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    if (decodeURIComponent(pair[0]) === variable) {
      return decodeURIComponent(pair[1]);
    }
  }
  return null;
};

export const crusherTypeParse = (type) => {
  let typeName;

  switch (type) {
    case 'crusher_cone':
      typeName = 'Cone Crusher';
      break;
    case 'crusher_jaw':
      typeName = 'Jaw Crusher';
      break;
    case 'crusher_horizontal_shaft':
      typeName = 'Horizontal Shaft Crusher';
      break;
    case 'crusher_vertical_shaft':
      typeName = 'Vertical Shaft Crusher';
      break;
    default:
      break;
  }

  return typeName;
};

/**
 * Performs download of file with it's base64 code
 * @param base64Code
 * @param fileName
 * @param contentType
 */
export const downloadFile = (base64Code, fileName, contentType) => {
  let fileLink;
  if (contentType === 'application/excel') {
    fileLink = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64Code}`;
  } else if (contentType === 'text/csv') {
    fileLink = `data:text/csv;base64,${base64Code}`;
  }

  const exportLinkElement = document.createElement('a');

  exportLinkElement.hidden = true;
  exportLinkElement.download = fileName;
  exportLinkElement.href = fileLink;

  exportLinkElement.click();
  exportLinkElement.remove();
};

/**
 * Performs download of file from uri
 * @param uri
 * @param name
 */
export const downloadUri = (uri, name) => {
  const link = document.createElement('a');
  link.download = name;
  link.href = uri;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Interval to aplly on components
 * @param callback {Function} The callback to be executed
 * @param delay {Number} The delay in ms to be considered
 */
export const useInterval = (callback, delay) => {
  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = callback;
  });
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    const id = setInterval(tick, delay);
    return () => clearInterval(id);
  }, [delay]);
};

export const filterArrayKeyValue = (array, searchFields, searchValue) => (
  array.filter(
    (item) => (
      // validates at least one filter criteria (every for all)
      searchFields.some(
        // search if some of the searchFields includes the searchValue
        (key) => item[key]?.toLowerCase().includes(searchValue),
      )
    ),
  )
);

/**
 * convert a hexdecimal color value to rgb color value
 * @param hexdecimal {string} hexdecimal color value
 * @return {string} rgb string format
 */
export const convertHexToRGB = (hexdecimal) => {
  const rgbHex = hexdecimal.replace('#', '').match(/.{1,2}/g);
  const rgb = [
    parseInt(rgbHex[0], 16),
    parseInt(rgbHex[1], 16),
    parseInt(rgbHex[2], 16),
  ];
  return rgb.join(',');
};

/**
 *
 * @param {Number} value temperature to be converted
 * @param {String} unit as string ex: 'fahrenheit' or 'celsius'
 * @returns {Number} new temperature converted value
 */
export const convertTemperature = (value, unit) => {
  let convertedValue = value;
  if (unit === 'fahrenheit') {
    convertedValue = (value * 1.8) + 32;
  } else if (unit === 'celsius') {
    convertedValue = ((value - 32) * 5) / 9;
  }
  return Math.round((convertedValue + Number.EPSILON) * 100) / 100;
};

export const convertDistance = (value, unit) => {
  let convertedValue = value;
  if (unit === 'millimeter') {
    convertedValue = value * 25.4;
  } else if (unit === 'inch') {
    convertedValue = value / 25.4;
  }
  return Math.round((convertedValue + Number.EPSILON) * 100) / 100;
};

export const convertSpeed = (value, unit) => {
  let convertedValue = value;
  if (unit === 'meterPerSecond') {
    convertedValue = value / 196.85039370078738;
  } else if (unit === 'footPerMinute') {
    convertedValue = value * 196.85039370078738;
  }
  return Math.round((convertedValue + Number.EPSILON) * 100) / 100;
};

export const convertPressure = (value, unit) => {
  let convertedValue = value;
  if (unit === 'poundPerSquareInch') {
    convertedValue = value * 0.0689475729;
  } else if (unit === 'bar') {
    convertedValue = value / 0.0689475729;
  }
  return Math.round((convertedValue + Number.EPSILON) * 100) / 100;
};

/**
 *
 * @param {Number} value weight to be converted
 * @param {String} unit as string ex: 'pounds' or 'kilogram'
 * @returns {Number} new weight converted value
 */
export const convertWeight = (value, unit) => {
  let convertedValue = value;
  if (unit === 'pounds') {
    convertedValue = value * 6.895;
  } else if (unit === 'kilopascal') {
    convertedValue = value / 6.895;
  }
  return Math.round(convertedValue);
};

export const getUserTimezone = () => (new Date().getTimezoneOffset() - (new Date().getTimezoneOffset() * 2)) / 60;

export const customerStatusDrawer = [
  { statusIcon: 'CreateAccount', text: 'view.ap.customers.deployment.mgntAccountRole' },
  { statusIcon: 'CustomerCognito', text: 'view.ap.customers.deployment.cognitoGroup' },
  { statusIcon: 'S3Buckets', text: 'view.ap.customers.deployment.s3Buckets' },
  { statusIcon: 'TransferFiles', text: 'view.ap.customers.deployment.transferFiles' },
  { statusIcon: 'VPCValues', text: 'view.ap.customers.deployment.vpcValues' },

  { statusIcon: 'WebACL', text: 'view.ap.customers.deployment.webACL' },
  { statusIcon: 'CloudTrail', text: 'view.ap.customers.deployment.cloudTrail' },
  { statusIcon: 'SQS', text: 'view.ap.customers.deployment.sqs' },
  { statusIcon: 'SNS', text: 'view.ap.customers.deployment.sns' },
  { statusIcon: 'VPCEndpoints', text: 'view.ap.customers.deployment.vpcEndpoints' },
  { statusIcon: 'IoTCore', text: 'view.ap.customers.deployment.iotCore' },
  { statusIcon: 'EC2Main', text: 'view.ap.customers.deployment.ec2Main' },

  { statusIcon: 'DynamoDBAlarmDevice', text: 'view.ap.customers.deployment.dynamoDB.alarmDevice' },
  { statusIcon: 'DynamoDBArea', text: 'view.ap.customers.deployment.dynamoDB.area' },
  { statusIcon: 'DynamoDBAsyncTask', text: 'view.ap.customers.deployment.dynamoDB.asyncTask' },
  { statusIcon: 'DynamoDBAsyncTaskSupport', text: 'view.ap.customers.deployment.dynamoDB.asyncTaskSupport' },
  { statusIcon: 'DynamoDBConveyor', text: 'view.ap.customers.deployment.dynamoDB.conveyor' },
  { statusIcon: 'DynamoDBConveyorIdler', text: 'view.ap.customers.deployment.dynamoDB.conveyorIdler' },
  { statusIcon: 'DynamoDBConveyorModule', text: 'view.ap.customers.deployment.dynamoDB.conveyorModule' },
  { statusIcon: 'DynamoDBDevicePropX', text: 'view.ap.customers.deployment.dynamoDB.devicePropX' },
  { statusIcon: 'DynamoDBDeviceRoll', text: 'view.ap.customers.deployment.dynamoDB.deviceRoll' },
  { statusIcon: 'DynamoDBHistoryEventDevice', text: 'view.ap.customers.deployment.dynamoDB.historyEventDevice' },
  { statusIcon: 'DynamoDBHistoryIoTDevice', text: 'view.ap.customers.deployment.dynamoDB.historyIoTDevice' },
  { statusIcon: 'DynamoDBNotificationEndpoint', text: 'view.ap.customers.deployment.dynamoDB.notificationEndpoint' },
  {
    statusIcon: 'DynamoDBNotificationSubscription',
    text: 'view.ap.customers.deployment.dynamoDB.notificationSubscription',
  },
  { statusIcon: 'DynamoDBPermission', text: 'view.ap.customers.deployment.dynamoDB.permissions' },
  { statusIcon: 'DynamoDBRollNonInstrumented', text: 'view.ap.customers.deployment.dynamoDB.rollNonInstrumented' },
  { statusIcon: 'DynamoDBSite', text: 'view.ap.customers.deployment.dynamoDB.site' },
  { statusIcon: 'DynamoDBUser', text: 'view.ap.customers.deployment.dynamoDB.user' },
  { statusIcon: 'DynamoDBUserGroup', text: 'view.ap.customers.deployment.dynamoDB.userGroup' },
  { statusIcon: 'DynamoDBUserLog', text: 'view.ap.customers.deployment.dynamoDB.userLog' },

  { statusIcon: 'EFS', text: 'view.ap.customers.deployment.efs' },
  { statusIcon: 'CustomerRequestUser', text: 'view.ap.customers.deployment.customerRequestUser' },
  { statusIcon: 'LambdaLayers', text: 'view.ap.customers.deployment.lambda.layers' },

  { statusIcon: 'LambdasIot', text: 'view.ap.customers.deployment.lambda.iot' },
  { statusIcon: 'LambdasAlarm', text: 'view.ap.customers.deployment.lambda.alarm' },

  { statusIcon: 'LambdasAsyncTaskConveyorAcknowledgement', text: 'view.ap.customers.deployment.lambda.asyncConvAck' },
  { statusIcon: 'LambdasAsyncTaskConveyorCreation', text: 'view.ap.customers.deployment.lambda.asyncConvCreation' },
  { statusIcon: 'LambdasAsyncTaskConveyorDeletion', text: 'view.ap.customers.deployment.lambda.asyncConvDeletion' },
  { statusIcon: 'LambdasAsyncTaskConveyorQrCode', text: 'view.ap.customers.deployment.lambda.asyncConvQRCode' },
  { statusIcon: 'LambdasAsyncTaskConveyorUpdate', text: 'view.ap.customers.deployment.lambda.asyncConvUpdate' },
  {
    statusIcon: 'LambdasAsyncTaskDeviceRollPreRegistration',
    text: 'view.ap.customers.deployment.lambda.rollPreRegistration',
  },

  { statusIcon: 'ApiAlarm', text: 'view.ap.customers.deployment.api.alarm' },
  { statusIcon: 'ApiArea', text: 'view.ap.customers.deployment.api.area' },
  { statusIcon: 'ApiAsyncTask', text: 'view.ap.customers.deployment.api.asyncTask' },
  { statusIcon: 'ApiConveyor', text: 'view.ap.customers.deployment.api.conveyor' },
  { statusIcon: 'ApiConveyorIdler', text: 'view.ap.customers.deployment.api.conveyorIdler' },
  { statusIcon: 'ApiConveyorModule', text: 'view.ap.customers.deployment.api.conveyorModule' },
  { statusIcon: 'ApiDevicePropX', text: 'view.ap.customers.deployment.api.devicePropX' },
  { statusIcon: 'ApiDeviceRoll', text: 'view.ap.customers.deployment.api.deviceRoll' },
  { statusIcon: 'ApiIot', text: 'view.ap.customers.deployment.api.iot' },
  { statusIcon: 'ApiMonitoring', text: 'view.ap.customers.deployment.api.monitoring' },
  { statusIcon: 'ApiReport', text: 'view.ap.customers.deployment.api.report' },
  { statusIcon: 'ApiRollNonInstrumented', text: 'view.ap.customers.deployment.api.rollNonInstrumented' },
  { statusIcon: 'ApiSite', text: 'view.ap.customers.deployment.api.site' },
  { statusIcon: 'ApiUser', text: 'view.ap.customers.deployment.api.user' },
  { statusIcon: 'ApiUserGroup', text: 'view.ap.customers.deployment.api.userGroup' },
  { statusIcon: 'ApiUserLog', text: 'view.ap.customers.deployment.api.userLog' },

  { statusIcon: 'SentinelDevice', text: 'view.ap.customers.deployment.sentinel.device' },
  { statusIcon: 'SentinelSystem', text: 'view.ap.customers.deployment.sentinel.system' },
  { statusIcon: 'SentinelUser', text: 'view.ap.customers.deployment.sentinel.user' },
];

export const isObject = (item) => item && typeof item === 'object' && !Array.isArray(item);

export const mergeDeep = (target, ...sources) => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep(target, ...sources);
};

export const safeJsonParse = (str) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return str;
  }
};

export const getHardwareTypeAndBearingId = (idDevice) => {
  // Extracts the hardware hex value from the idDevice string
  const hardwareHex = parseInt(idDevice.substring(8, 10), 16);

  let hardwareType = 'single';
  let bearingId = null;

  if (hardwareHex >= 0xC0 && hardwareHex <= 0xDF) {
    hardwareType = 'double';
    bearingId = 'A';
  } else if (hardwareHex >= 0xE0 && hardwareHex <= 0xFF) {
    hardwareType = 'double';
    bearingId = 'B';
  }

  return { hardwareType, bearingId };
};
